import * as Immutable from "immutable";
import * as PropTypes from "prop-types";
import * as React from "react";
import * as querystring from "querystring";
import * as url from "url";
import AutoComplete from "material-ui/AutoComplete";
import Bottom from "./bottom";
import FilterRecord from "../../records/filter/filter";
import GenericSearch from "collections/generic";
import theme from "styles/theme";
import { $, merge } from "glamor";
import { CSSProperties, useMemo } from "react";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import { ListItem, ListItemText, Collapse, Badge, Chip, List as MuiList } from "@material-ui/core";
import { debounce } from "lodash";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import { toDataTest } from "helpers/test";

export const CONVERT_TO_ID_DELIMITER = "__to_id__";
const LIMIT = 50; // optimization for filters with small amount of entries
const WAIT_BEFORE_FILTER_FETCH = 1000;

export class SearchField extends React.PureComponent<any, any> {
    static propTypes = {
        filter: PropTypes.object.isRequired,
        activeValues: PropTypes.array.isRequired,
        filtersDisabled: PropTypes.bool,
        onAdd: PropTypes.func.isRequired,
        inputStyle: PropTypes.object,
        useURLQuery: PropTypes.bool,
        convertToId: PropTypes.bool,
    };

    styles: any = {
        autoComplete: {
            display: "block",
            fontSize: "14px",
        },
        input: {
            border: "1px solid " + theme.palette.lightGrey,
            padding: "0px 0px 0px 8px",
            height: "50px",
            borderRadius: "4px",
            textTransform: "none",
            width: "100%",
        },
        hint: {
            padding: "20px 16px 10px",
            width: "100%",
            bottom: "0px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        menu: merge(
            $(" span > div > div > div", {
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
            })
        ),
    };

    _autoComplete: AutoComplete;

    constructor(props) {
        super(props);
        const parsedUrl = url.parse(this.props.filter.__uri);
        const Search = GenericSearch.extend({ url: props.useURLQuery ? parsedUrl.path : parsedUrl.pathname });
        this.state = {
            search: new Search(),
            autoCompleteValues: [],
            completeValuesWithIds: [],
        };

        if (props.inputStyle) {
            this.styles.input = { ...this.styles.input, ...props.inputStyle };
            this.styles.autoCompleteContainer = merge({
                display: "block",
                // eslint-disable-next-line import/no-named-as-default-member
                padding: `0 ${theme.rawTheme.spacing.desktopGutterLess}px`,
                ...(props.inputContainerStyle || {}),
            });
        }

        this.fetchFilterValues = debounce(this.fetchFilterValues, WAIT_BEFORE_FILTER_FETCH);
        this.fetchFilterValues();
    }

    handleAddItem = (value, index) => {
        if (index !== -1) {
            this.fetchFilterValues();
            this._autoComplete.setState({
                searchText: "",
                open: false,
            });

            if (this.props.convertToId) {
                const id = this.state.completeValuesWithIds.find((item) => item.__name === value)?.__id;
                this.props.onAdd(value, id);
            } else {
                this.props.onAdd(value);
            }
        }
    };

    fetchFilterValues = (value = "") => {
        const { search } = this.state;
        if (this.state.valuesLimit) {
            return;
        }
        const query = querystring.parse(this.props.filter.__query);
        query[this.props.filter.__uri_key] = value;
        query.__limit = LIMIT.toString();
        search.fetch({
            data: query,
            reset: true,
            success: () => {
                if (!this._autoComplete) {
                    return;
                }
                const autoCompleteValues = search.map("__name").sort();

                const completeValuesWithIds = this.props.convertToId
                    ? search
                          .map((item) => ({
                              __name: item.__name,
                              __id: item.__id,
                          }))
                          .sort((firstItem, secondItem) => firstItem.__name.localeCompare(secondItem.__name))
                    : [];

                if (!this.state.valuesLimit && !value) {
                    this.setState({ valuesLimit: autoCompleteValues.length < LIMIT });
                }
                this.setState({ autoCompleteValues });
                this.setState({ completeValuesWithIds });
            },
        });
    };

    render() {
        const { filter, activeValues, convertToId } = this.props;
        const { autoCompleteValues, valuesLimit } = this.state;
        const convertedActiveValues = convertToId
            ? activeValues.map((value) =>
                  value.includes(CONVERT_TO_ID_DELIMITER) ? value.split(CONVERT_TO_ID_DELIMITER)[0] : value
              )
            : activeValues;
        const dataSource = autoCompleteValues.filter((value) => !convertedActiveValues.includes(value));

        return (
            <div {...this.styles.autoCompleteContainer}>
                <AutoComplete
                    ref={(ref) => (this._autoComplete = ref)}
                    dataSource={dataSource}
                    filter={valuesLimit ? AutoComplete.fuzzyFilter : AutoComplete.noFilter}
                    hintText={formatMessage("Search")}
                    fullWidth={true}
                    inputStyle={this.styles.input as CSSProperties}
                    hintStyle={this.styles.hint as CSSProperties}
                    style={this.styles.autoComplete}
                    menuProps={{ maxHeight: 300, className: "ignoreDeselect" }}
                    openOnFocus={true}
                    onNewRequest={this.handleAddItem}
                    onUpdateInput={this.fetchFilterValues}
                    menuCloseDelay={0}
                    underlineShow={false}
                    popoverProps={{ canAutoPosition: true }}
                    data-test={`auto-complete-${toDataTest(filter.__key)}`}
                />
            </div>
        );
    }
}

interface ISearchChipProps {
    item: string;
    filtersDisabled: boolean;

    handleItemRemove: (item: string) => void;
    convertToId?: boolean;
}

export const SearchChip = ({ item, filtersDisabled, handleItemRemove, convertToId }: ISearchChipProps) => {
    const styles = useMemo(
        () => ({
            labelStyle: {
                textOverflow: "ellipsis",
                width: "100%",
                overflow: "hidden",
                color: "rgb(88, 88, 88)",
                fontFamily: "Lato, sans-serif",
                fontSize: "14px",
            },
            chip: merge(
                {
                    margin: "12px 0 0 16px",
                    maxWidth: "calc(100% - 32px)",
                },
                $(" .MuiChip-label", {
                    width: "90%",
                })
            ),
        }),
        []
    );

    const onDelete = filtersDisabled ? undefined : () => handleItemRemove(item);
    const label = item.includes(CONVERT_TO_ID_DELIMITER) ? item.split(CONVERT_TO_ID_DELIMITER)[0] : item;

    return (
        <Chip
            onDelete={onDelete}
            data-test={DATATEST.GALLERY.FILTER_CHIP}
            label={<span style={styles.labelStyle}>{convertToId ? label : item}</span>}
            {...styles.chip}
        />
    );
};

interface IAutocompleteProps {
    lastFilter: boolean;
    filtersDisabled: boolean;
    useURLQuery: boolean;

    filter: FilterRecord;
    activeFiltersValues: Immutable.Map<any, any>;

    onChange: (activeFiltersValues: Immutable.Map<any, any>) => void;
}

interface IAutocompleteState {
    open: boolean;
    autoCompleteValues: any[];
    completeValuesWithIds?: any[];
}

export class AutoCompleteFilter extends React.PureComponent<IAutocompleteProps, IAutocompleteState> {
    static defaultProps = {
        lastFilter: false,
        filtersDisabled: false,
    };

    styles = {
        nestedListItem: {
            backgroundColor: "transparent",
        },
        listItem: {
            fontSize: "14px",
            margin: 0,
            padding: "3px 16px 3px 8px",
            color: theme.palette.darkerGrey,
        },
        listItemInner: {
            padding: "10px 0px 10px 8px",
        },
        dropdownIcon: {
            fontSize: "24px",
            color: "rgb(117, 117, 117)",
            transition: "transform .1s",
        },
        dropdownIconOpen: {
            msTransform: "rotate(-90deg)",
            WebkitTransform: "rotate(-90deg)",
            transform: "rotate(-90deg)",
        },
        dropdownIconDisabled: {
            display: "none",
        },
        badgeContainer: merge(
            {
                position: "relative",
                width: "90%",
            },
            $(" .MuiBadge-root", {
                position: "static",
                padding: "0px",
            }),
            $(" .MuiBadge-badge", {
                top: 10,
                fontSize: "12px",
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                color: theme.palette.darkerGrey,
                backgroundColor: theme.palette.lightGrey,
                fontFamily: "Lato, sans-serif",
            })
        ),
        label: {
            fontSize: "14px",
            fontFamily: "Lato, sans-serif",
        },
        listOpen: {
            backgroundColor: "white",
        },
    };

    constructor(props) {
        super(props);
        this.state = {
            open: this.activeValues.size > 0 || props.filter.__default_open,
            autoCompleteValues: [],
            completeValuesWithIds: [],
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.filtersDisabled &&
            !this.state.open &&
            !this.props.activeFiltersValues.size &&
            nextProps.activeFiltersValues.size
        ) {
            // we've switched into advanced search and set a value, for consistency, open filter container
            this.setState({ open: true });
        }
    }

    get activeValues() {
        const { filter, activeFiltersValues } = this.props;
        let values = activeFiltersValues.get(filter.__key);
        if (values) {
            if (typeof values === "string") {
                values = Immutable.List.of(values);
            }
        } else {
            values = Immutable.List();
        }
        return values;
    }

    handleToggleOpen = () => {
        this.setState({ open: !this.state.open });
    };

    handleItemAdded = (value, id?) => {
        const { activeFiltersValues, filter, onChange } = this.props;
        const activeFilterValues = this.activeValues;

        if (filter.__convert_to_id && id && !activeFilterValues.includes(`${value}${CONVERT_TO_ID_DELIMITER}${id}`)) {
            onChange(
                activeFiltersValues.set(
                    filter.__key,
                    activeFilterValues.push(`${value}${CONVERT_TO_ID_DELIMITER}${id}`)
                )
            );
        } else if (!activeFilterValues.includes(value)) {
            onChange(activeFiltersValues.set(filter.__key, activeFilterValues.push(value)));
        }
    };

    handleItemRemove = (value) => {
        const { activeFiltersValues, filter, onChange, filtersDisabled } = this.props;
        if (!filtersDisabled) {
            const list = this.activeValues.filter((val) => val !== value);
            onChange(
                list.size > 0 ? activeFiltersValues.set(filter.__key, list) : activeFiltersValues.delete(filter.__key)
            );
        }
    };

    render() {
        const { filter, lastFilter, filtersDisabled, useURLQuery } = this.props;
        const { open } = this.state;
        const activeFilterValues = this.activeValues;
        const styles = this.styles;
        const convertToId = filter.__convert_to_id || false;

        if (activeFilterValues.size === 0 && filtersDisabled) {
            return null;
        }

        const count = activeFilterValues.size;
        const plainTitle = <span style={styles.label}>{filter.__name}</span>;
        const badgeTitle = (
            <div {...styles.badgeContainer}>
                <Badge badgeContent={count} data-test={`filter-${filter.__key}-badge`} color="primary">
                    {plainTitle}
                </Badge>
            </div>
        );
        const title = count > 0 ? badgeTitle : plainTitle;

        return (
            <React.Fragment>
                <ListItem
                    disabled={filtersDisabled}
                    className="selectFilter"
                    button={true}
                    onClick={this.handleToggleOpen}
                    style={{
                        ...styles.listItem,
                        ...(open ? styles.listOpen : {}),
                    }}
                >
                    <ListItemText primary={title} />
                    <ChevronLeft
                        style={{
                            ...styles.dropdownIcon,
                            ...(open ? styles.dropdownIconOpen : {}),
                        }}
                        data-test={`chevron-${toDataTest(filter.__key)}`}
                    />
                </ListItem>

                <Collapse
                    in={open}
                    unmountOnExit={true}
                    timeout={100}
                    style={{
                        ...styles.nestedListItem,
                        ...(open ? styles.listOpen : {}),
                    }}
                >
                    <MuiList>
                        {open && !filtersDisabled && (
                            <SearchField
                                key="auto-complete"
                                filter={filter}
                                activeValues={activeFilterValues.toArray()}
                                onAdd={this.handleItemAdded}
                                useURLQuery={useURLQuery}
                                convertToId={convertToId}
                            />
                        )}

                        {activeFilterValues.map((item, index) => (
                            <SearchChip
                                key={index}
                                item={item}
                                filtersDisabled={filtersDisabled}
                                handleItemRemove={this.handleItemRemove}
                                convertToId={convertToId}
                            />
                        ))}

                        {!lastFilter && <Bottom key="bottom" />}
                    </MuiList>
                </Collapse>
            </React.Fragment>
        );
    }
}
